import sodium from 'libsodium-wrappers-sumo';

export async function ready(): Promise<void> {
  await sodium.ready;
}

export interface KeyPair {
  publicKey: Uint8Array;
  privateKey: Uint8Array;
}

export function b64encode(buf: Uint8Array): string {
  return sodium.to_base64(buf, sodium.base64_variants.ORIGINAL);
}
export function b64decode(s: string): Uint8Array {
  return sodium.from_base64(s, sodium.base64_variants.ORIGINAL);
}

export function generateKeyPair(): KeyPair {
  const kp = sodium.crypto_box_keypair();
  return { publicKey: kp.publicKey, privateKey: kp.privateKey };
}

// Sincrono — fallback usado por deriveFrequencyKey y como respaldo si no hay Workers.
function deriveKeyFromPasswordSync(password: string, salt: Uint8Array): Uint8Array {
  return sodium.crypto_pwhash(
    sodium.crypto_secretbox_KEYBYTES,
    password,
    salt,
    sodium.crypto_pwhash_OPSLIMIT_MODERATE,
    sodium.crypto_pwhash_MEMLIMIT_MODERATE,
    sodium.crypto_pwhash_ALG_ARGON2ID13,
  );
}

// Worker pool ligero (1 worker reutilizable).
let argonWorker: Worker | null = null;
let argonSeq = 0;
const argonPending = new Map<number, { resolve: (k: Uint8Array) => void; reject: (e: Error) => void }>();

function getArgonWorker(): Worker | null {
  if (typeof Worker === 'undefined') return null;
  if (argonWorker) return argonWorker;
  try {
    argonWorker = new Worker(new URL('./workers/argon2.worker.ts', import.meta.url), { type: 'module' });
    argonWorker.onmessage = (e: MessageEvent<{ id: number; ok: boolean; key?: ArrayBuffer; error?: string }>) => {
      const { id, ok, key, error } = e.data;
      const p = argonPending.get(id);
      if (!p) return;
      argonPending.delete(id);
      if (ok && key) p.resolve(new Uint8Array(key));
      else p.reject(new Error(error || 'argon2_worker_error'));
    };
    argonWorker.onerror = () => {
      // Si el worker peta, fallback inline.
      argonWorker = null;
    };
    return argonWorker;
  } catch {
    argonWorker = null;
    return null;
  }
}

// API pública: ahora async. Usa worker si está disponible; si no, derive sync.
export async function deriveKeyFromPassword(password: string, salt: Uint8Array): Promise<Uint8Array> {
  await sodium.ready;
  const w = getArgonWorker();
  if (!w) return deriveKeyFromPasswordSync(password, salt);
  const id = ++argonSeq;
  // Copia el buffer del salt antes de transferir para no romper el caller.
  const saltCopy = new Uint8Array(salt);
  return new Promise<Uint8Array>((resolve, reject) => {
    argonPending.set(id, { resolve, reject });
    try {
      w.postMessage({ id, password, salt: saltCopy.buffer }, [saltCopy.buffer]);
    } catch (err) {
      argonPending.delete(id);
      // Fallback inline si postMessage falla.
      try { resolve(deriveKeyFromPasswordSync(password, salt)); }
      catch (e) { reject(e as Error); }
    }
  });
}

const FREQ_SALT = (() => {
  const tag = new TextEncoder().encode('ikognit-freq-v1');
  const out = new Uint8Array(16);
  out.set(tag.slice(0, 16));
  return out;
})();

export function deriveFrequencyKey(frequency: string): Uint8Array {
  return sodium.crypto_pwhash(
    32,
    frequency,
    FREQ_SALT,
    sodium.crypto_pwhash_OPSLIMIT_MODERATE,
    sodium.crypto_pwhash_MEMLIMIT_MODERATE,
    sodium.crypto_pwhash_ALG_ARGON2ID13,
  );
}

export function hmacTag(freqKey: Uint8Array, ikognitId: string): Uint8Array {
  const fullMac = sodium.crypto_auth(ikognitId, freqKey);
  return fullMac.slice(0, 16);
}

export function generalTag(ikognitId: string): Uint8Array {
  return new TextEncoder().encode(ikognitId);
}

/**
 * Secreto efectivo de un IKO dentro de un DOMINIO.
 *
 * En IKOGNIT (el general) el secreto efectivo es, literalmente, lo que la
 * persona escribe. Dentro de un dominio se mezcla con la clave del dominio:
 *
 *     effSecret = base64( crypto_auth("ikognit-domain-iko-v1::" + palabra, domainKey) )
 *
 * Tres consecuencias, las tres queridas:
 *
 *  · Los IKO de un dominio son planos DISTINTOS de los del general aunque la
 *    palabra coincida. «cumpleaños» en la familia y «cumpleaños» en IKOGNIT no
 *    tienen nada que ver.
 *  · Una palabra floja —y en una familia las palabras son flojas— queda
 *    respaldada por los 32 bytes de entropía de la clave del dominio.
 *  · Quien no es del dominio no puede derivar sus planos ni sabiendo la palabra,
 *    porque no tiene la clave.
 *
 * La mezcla ocurre SOLO en el dispositivo. Al servidor le sigue llegando la
 * misma sopa global de pseudónimos, sin saber cuáles son de qué dominio: el
 * plano ciego no está particionado y esto no lo particiona.
 */
export function secretoDeIkoEnDominio(palabra: string, domainKeyB64: string): string {
  const clave = sodium.from_base64(domainKeyB64, sodium.base64_variants.ORIGINAL);
  const mac = sodium.crypto_auth('ikognit-domain-iko-v1::' + palabra, clave);
  return sodium.to_base64(mac, sodium.base64_variants.ORIGINAL);
}

// Para sealed sender en frecuencia general: sender_tag aleatorio por mensaje.
// El receptor identifica al emisor SOLO descifrando sender_envelope con su privateKey.
export function randomTag(): Uint8Array {
  return sodium.randombytes_buf(32);
}

export function decodeGeneralTag(tag: Uint8Array): string {
  return new TextDecoder().decode(tag);
}

export function sealedBoxEncrypt(plaintext: string, recipientPublicKey: Uint8Array): Uint8Array {
  return sodium.crypto_box_seal(sodium.from_string(plaintext), recipientPublicKey);
}

export function sealedBoxDecrypt(
  ciphertext: Uint8Array,
  recipientPublicKey: Uint8Array,
  recipientPrivateKey: Uint8Array,
): string {
  const plain = sodium.crypto_box_seal_open(ciphertext, recipientPublicKey, recipientPrivateKey);
  return sodium.to_string(plain);
}

export function wrapPrivateKey(privateKey: Uint8Array, key: Uint8Array): { ciphertext: Uint8Array; nonce: Uint8Array } {
  const nonce = sodium.randombytes_buf(sodium.crypto_secretbox_NONCEBYTES);
  const cipher = sodium.crypto_secretbox_easy(privateKey, nonce, key);
  const blob = new Uint8Array(nonce.length + cipher.length);
  blob.set(nonce, 0);
  blob.set(cipher, nonce.length);
  return { ciphertext: blob, nonce };
}

export function unwrapPrivateKey(blob: Uint8Array, key: Uint8Array): Uint8Array {
  const nonce = blob.slice(0, sodium.crypto_secretbox_NONCEBYTES);
  const cipher = blob.slice(sodium.crypto_secretbox_NONCEBYTES);
  return sodium.crypto_secretbox_open_easy(cipher, nonce, key);
}

export function newSalt(): Uint8Array {
  return sodium.randombytes_buf(sodium.crypto_pwhash_SALTBYTES);
}

export function encryptMessage(
  plaintext: string,
  recipientPublicKey: Uint8Array,
  senderPrivateKey: Uint8Array,
): { nonce: Uint8Array; ciphertext: Uint8Array } {
  const nonce = sodium.randombytes_buf(sodium.crypto_box_NONCEBYTES);
  const ciphertext = sodium.crypto_box_easy(
    sodium.from_string(plaintext),
    nonce,
    recipientPublicKey,
    senderPrivateKey,
  );
  return { nonce, ciphertext };
}

export function decryptMessage(
  ciphertext: Uint8Array,
  nonce: Uint8Array,
  senderPublicKey: Uint8Array,
  recipientPrivateKey: Uint8Array,
): string {
  const plain = sodium.crypto_box_open_easy(ciphertext, nonce, senderPublicKey, recipientPrivateKey);
  return sodium.to_string(plain);
}

// === Media ===

// Genera una clave aleatoria de 32 bytes para cifrar un archivo con secretbox.
export function newFileKey(): Uint8Array {
  return sodium.randombytes_buf(sodium.crypto_secretbox_KEYBYTES);
}

// Cifra el contenido entero del archivo y devuelve [nonce(24) | ciphertext].
export function encryptFile(plain: Uint8Array, key: Uint8Array): Uint8Array {
  const nonce = sodium.randombytes_buf(sodium.crypto_secretbox_NONCEBYTES);
  const cipher = sodium.crypto_secretbox_easy(plain, nonce, key);
  const out = new Uint8Array(nonce.length + cipher.length);
  out.set(nonce, 0);
  out.set(cipher, nonce.length);
  return out;
}

export function decryptFile(blob: Uint8Array, key: Uint8Array): Uint8Array {
  const nonce = blob.slice(0, sodium.crypto_secretbox_NONCEBYTES);
  const cipher = blob.slice(sodium.crypto_secretbox_NONCEBYTES);
  return sodium.crypto_secretbox_open_easy(cipher, nonce, key);
}

// =============================================================
// F1.2 — Signing keys (Ed25519) for prekey signatures
// =============================================================

export interface SigningKeyPair {
  publicKey: Uint8Array;   // 32 bytes
  privateKey: Uint8Array;  // 64 bytes (libsodium Ed25519 private key format)
}

export function generateSigningKeyPair(): SigningKeyPair {
  const kp = sodium.crypto_sign_keypair();
  return { publicKey: kp.publicKey, privateKey: kp.privateKey };
}

export function signDetached(message: Uint8Array, signPrivateKey: Uint8Array): Uint8Array {
  return sodium.crypto_sign_detached(message, signPrivateKey);
}

export function verifyDetached(signature: Uint8Array, message: Uint8Array, signPublicKey: Uint8Array): boolean {
  try {
    return sodium.crypto_sign_verify_detached(signature, message, signPublicKey);
  } catch {
    return false;
  }
}

// =============================================================
// F1.2 — DH (X25519) raw scalar mult, used in X3DH
// =============================================================

export function x25519Dh(theirPublicKey: Uint8Array, ourPrivateKey: Uint8Array): Uint8Array {
  return sodium.crypto_scalarmult(ourPrivateKey, theirPublicKey);
}

// X25519 keypair generation independent of crypto_box (same primitive).
export function generateX25519KeyPair(): { publicKey: Uint8Array; privateKey: Uint8Array } {
  const priv = sodium.randombytes_buf(32);
  // Clamp scalar per RFC 7748.
  priv[0] &= 248;
  priv[31] &= 127;
  priv[31] |= 64;
  const pub = sodium.crypto_scalarmult_base(priv);
  return { publicKey: pub, privateKey: priv };
}

// =============================================================
// F1.2 — HKDF-SHA512 over libsodium primitives.
// =============================================================

import { hkdf as nobleHkdf, extract as nobleHkdfExtract, expand as nobleHkdfExpand } from '@noble/hashes/hkdf.js';
import { sha512 } from '@noble/hashes/sha2.js';
import { hmac as nobleHmac } from '@noble/hashes/hmac.js';

export function hkdfExtract(salt: Uint8Array, ikm: Uint8Array): Uint8Array {
  // HKDF-Extract con SHA-512: HMAC(salt, IKM). Salt vacío → 64 bytes de ceros (RFC 5869).
  return nobleHkdfExtract(sha512, ikm, salt.length === 0 ? new Uint8Array(64) : salt);
}

export function hkdfExpand(prk: Uint8Array, info: Uint8Array, length: number): Uint8Array {
  return nobleHkdfExpand(sha512, prk, info, length);
}

export function hkdf(ikm: Uint8Array, salt: Uint8Array, info: Uint8Array, length: number): Uint8Array {
  return nobleHkdf(sha512, ikm, salt.length === 0 ? new Uint8Array(64) : salt, info, length);
}

// ── Separación entre lo que ABRE y lo que AUTENTICA ──────────────────────────
//
// Hasta el 2026-08-05 el cliente mandaba la contraseña EN CLARO a /auth/login.
// El servidor guardaba solo su hash argon2id, pero la recibía en cada entrada, y
// además tiene `private_key_salt` y `private_key_encrypted`. Con esas tres cosas
// a la vez podía derivar la wrappingKey y abrir la clave privada de la cuenta:
// todo el plano general quedaba legible para nosotros. No hacía falta guardar
// nada — bastaba una línea de más en el servidor, y desde fuera nadie podría
// saber si estaba o no.
//
// Ahora la contraseña no sale del dispositivo. Del mismo Argon2id salen dos
// cosas distintas:
//
//   K          = Argon2id(password, salt)      ← caro, se calcula una sola vez
//   wrapKey    = K                             ← NO SALE NUNCA. Abre la clave privada.
//   authSecret = HKDF(K, salt, "ikognit-auth-v1")  ← esto es lo único que viaja
//
// El servidor hashea y verifica `authSecret`. HKDF es de un solo sentido, así
// que de authSecret no se puede volver a K: aunque el servidor guarde todo lo
// que recibe, no puede abrir nada.
//
// `wrapKey` se deja EXACTAMENTE igual que antes a propósito: así los
// private_key_encrypted que ya existen siguen abriendo y no hay que recifrar
// nada de lo guardado. Lo que cambia es solo la autenticación.
//
// El coste es cero: authSecret sale de un HKDF sobre K, no de un segundo
// Argon2id. El KDF caro se sigue ejecutando una única vez por entrada.
const INFO_AUTH = new TextEncoder().encode('ikognit-auth-v1');

export function deriveAuthSecret(wrappingKey: Uint8Array, salt: Uint8Array): Uint8Array {
  return hkdf(wrappingKey, salt, INFO_AUTH, 32);
}

// HMAC-SHA-512 con clave de tamaño arbitrario (libsodium-sumo no expone esto directamente).
export function hmacSha512(key: Uint8Array, data: Uint8Array): Uint8Array {
  return nobleHmac(sha512, key, data);
}

export function concatBytes(...arrays: Uint8Array[]): Uint8Array {
  const total = arrays.reduce((s, a) => s + a.length, 0);
  const out = new Uint8Array(total);
  let off = 0;
  for (const a of arrays) { out.set(a, off); off += a.length; }
  return out;
}
